import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Box, Paper, Grid, CircularProgress, Checkbox, Typography, TextField, FormControlLabel, Button } from "@material-ui/core";
import { defineMessages, useIntl, FormattedNumber, FormattedMessage } from "react-intl";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateUtils from "@date-io/luxon";
import WarningRounded from "@material-ui/icons/WarningRounded";
import ReplayIcon from "@material-ui/icons/Replay";
import { BaseResource } from "@ploy-lib/rest-resources";
import { apiResourceUrl } from "@ploy-lib/core";
import { useResource } from "@rest-hooks/core";
const messages = defineMessages({
    fulfillmentHeader: {
        id: "fulfillment-header",
        defaultMessage: "Calculate the fullfilment",
        description: "Header text for fulfillment view"
    },
    regNumberLabel: {
        id: "fulfillment-reg-number-label",
        defaultMessage: "Registration number",
        description: "Label for registration number field"
    },
    phoneNumberLabel: {
        id: "fulfillment-phone-number",
        defaultMessage: "Customer's mobile number",
        description: "Label for phone number field"
    },
    checkboxLabel: {
        id: "fulfillment-checkbox-label",
        defaultMessage: "The customer has approved that I collect the redemption sum and store this for 14 days, and I am aware that the customer will be notified via SMS about this",
        description: "Label for customer consent chekcbox"
    },
    dateLabel: {
        id: "fulfillment-date-label",
        defaultMessage: "Date"
    },
    buttonText: {
        id: "fulfillment-button-text",
        defaultMessage: "Calculate",
        description: "Button text for getting fulfillment"
    },
    amountLabel: {
        id: "fulfillment-amount-label",
        defaultMessage: "Amount:"
    },
    accountInfoHeader: {
        id: "fulfillment-account-info-header",
        defaultMessage: "Payment Information:"
    },
    accountLabel: {
        id: "fulfillment-account-label",
        defaultMessage: "Account no:"
    },
    customerIdentificationLabel: {
        id: "fulfillment-customer-identifiction-label",
        defaultMessage: "KID number:"
    },
    markWithMessage: {
        id: "fulfillment-mark-with-message",
        defaultMessage: "Marked with: Fulfillment "
    },
    warningMessage: {
        id: "fulfillment-warning-message",
        defaultMessage: "It is assumed that the amount will be paid by this date for the loan to be repaid"
    },
    noResultsMessage: {
        id: "fulfillment-no-results-message",
        defaultMessage: "Unable to enter redemption amount, contact us by phone: 12354678"
    },
    newSearchButtonText: {
        id: "fulfillment-start-over-button-text",
        defaultMessage: "Start over",
        description: "Button text after a search has been made, to start a new one"
    },
    noContractFoundMessage: {
        id: "fulfillment-no-contract-found-message",
        defaultMessage: "No contract was found for the provided registration number"
    },
    regNumberInvalidMessage: {
        id: "fulfillment-reg-number-invalid",
        defaultMessage: "Requires a valid number"
    },
    phoneNumberInvalidMessage: {
        id: "fulfillment-phone-number-invalid",
        defaultMessage: "Requires a valid number"
    },
    invalidContractTypeMessage: {
        id: "fulfillment-invalid-contract-type-message",
        defaultMessage: "Kontrakten er ikke tilgjengelig for innfrielse"
    },
    resultHeader: {
        id: "fulfillment-results-header",
        defaultMessage: "Fulfillment amount per {searchDate, date, long} at {regNumber}"
    }
});
class FulfillmentSettingsResource extends BaseResource {
    constructor() {
        super(...arguments);
        this.showPhoneNumber = false;
        this.showDate = false;
        this.maxDays = 365;
        this.showCarMake = false;
    }
    pk() {
        return "settings";
    }
}
FulfillmentSettingsResource.urlRoot = apiResourceUrl("fulfillment");
export const Fulfillment = ({ getFulfillment, checkboxLabel, warningMessage }) => {
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState();
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [disabled, setDisabled] = React.useState(false);
    const [regNumber, setRegNumber] = React.useState("");
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [checked, setChecked] = React.useState(false);
    const [result, setResult] = React.useState();
    const [fulfillmentDate, setFulfillmentDate] = React.useState();
    const dateToday = new Date();
    const intl = useIntl();
    const [regNumberBlurred, setRegNumberBlurred] = React.useState(false);
    const [phoneNumberBlurred, setPhoneNumberBlurred] = React.useState(false);
    const regNumberValid = regNumber.length >= 6;
    const phoneNumberValid = phoneNumber.length >= 8;
    const handleChangeRegNumber = (event) => {
        setRegNumber(event.target.value);
    };
    const handleChangePhoneNumber = (event) => {
        setPhoneNumber(event.target.value);
    };
    const handleChangeFulfillmentDate = (fulfillmentDate) => {
        setFulfillmentDate(fulfillmentDate ?? undefined);
    };
    const utils = new DateUtils();
    const handleReset = () => {
        setDisabled(false);
        setResult(undefined);
        setFailed(undefined);
        setErrorMessage(null);
        setChecked(false);
        setRegNumberBlurred(false);
        setPhoneNumberBlurred(false);
    };
    const handleSearch = async () => {
        setLoading(true);
        setDisabled(true);
        const obj = {
            registrationNumber: regNumber,
            phoneNumber,
            fulfillmentDate: fulfillmentDate?.toJSDate()
        };
        try {
            const response = await getFulfillment(obj);
            response.registrationNumber = regNumber;
            setResult(response);
            setFailed(false);
            setErrorMessage(null);
        }
        catch (err) {
            setDisabled(false);
            setFailed(true);
            setErrorMessage(err?.message ?? null);
        }
        setLoading(false);
    };
    const settings = useResource(FulfillmentSettingsResource.detail(), {});
    return (_jsxs(Grid, { container: true, spacing: 2, justifyContent: "space-around", children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(Paper, { elevation: 0, children: _jsxs(Box, { p: 2, children: [_jsx(Box, { p: 2, children: _jsx(Typography, { variant: "h5", children: intl.formatMessage(messages.fulfillmentHeader) }) }), _jsx(Box, { p: 1, style: { display: "inline-block" }, children: _jsx(TextField, { id: "fulfillment-reg-number", label: intl.formatMessage(messages.regNumberLabel), value: regNumber, onChange: e => handleChangeRegNumber(e), variant: "outlined", placeholder: "", InputLabelProps: { shrink: true }, disabled: disabled, onBlur: () => setRegNumberBlurred(true), error: regNumberBlurred && !regNumberValid, helperText: regNumberBlurred && !regNumberValid
                                        ? intl.formatMessage(messages.regNumberInvalidMessage)
                                        : undefined }) }), settings.showPhoneNumber ? (_jsx(Box, { p: 1, style: { display: "inline-block" }, children: _jsx(TextField, { id: "fulfillment-phone-number", label: intl.formatMessage(messages.phoneNumberLabel), value: phoneNumber, onChange: e => handleChangePhoneNumber(e), variant: "outlined", placeholder: "", InputLabelProps: { shrink: true }, disabled: disabled, onBlur: () => setPhoneNumberBlurred(true), error: phoneNumberBlurred && !phoneNumberValid, helperText: phoneNumberBlurred && !phoneNumberValid
                                        ? intl.formatMessage(messages.phoneNumberInvalidMessage)
                                        : undefined }) })) : null, settings.showDate ? (_jsx(Box, { p: 1, style: { display: "inline-block" }, children: _jsx(MuiPickersUtilsProvider, { utils: DateUtils, children: _jsx(KeyboardDatePicker, { id: "fulfillment-date", label: intl.formatMessage(messages.dateLabel), value: fulfillmentDate, onChange: handleChangeFulfillmentDate, variant: "dialog", format: "dd/MM/yyyy", margin: "none", InputLabelProps: { shrink: true }, disabled: disabled, inputVariant: "outlined", disablePast: true, maxDate: settings.maxDays
                                            ? utils.date(dateToday).plus({ days: settings.maxDays })
                                            : undefined }) }) })) : null, _jsx(Box, { p: 1, children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: checked, onChange: () => setChecked(!checked), disabled: disabled }), label: checkboxLabel ?? intl.formatMessage(messages.checkboxLabel) }) }), _jsx(Box, { p: 1, mt: 1, children: _jsxs(Box, { display: "inline-flex", alignItems: "center", style: { minHeight: 36 }, children: [!loading && (result !== undefined || failed) ? (_jsx(Button, { color: "primary", variant: "outlined", onClick: handleReset, startIcon: _jsx(ReplayIcon, {}), children: intl.formatMessage(messages.newSearchButtonText) })) : (_jsx(Button, { color: "primary", variant: "outlined", onClick: () => handleSearch(), disabled: disabled ||
                                                !checked ||
                                                !regNumberValid ||
                                                (settings.showPhoneNumber && !phoneNumberValid) ||
                                                (settings.showDate &&
                                                    (fulfillmentDate === undefined ||
                                                        utils.isBefore(fulfillmentDate, utils.date(dateToday)) ||
                                                        settings.maxDays
                                                        ? utils.isAfter(fulfillmentDate, utils
                                                            .date(dateToday)
                                                            .plus({ days: settings.maxDays }))
                                                        : false)), children: intl.formatMessage(messages.buttonText) })), loading && (_jsx(CircularProgress, { size: 24, style: { marginLeft: 8 } }))] }) })] }) }) }), !!!failed ? (result &&
                result.fulfillmentInformation &&
                result.fulfillmentInformation.length > 0 ? (result.fulfillmentInformation.map((x, i) => {
                return (_jsx(Grid, { item: true, xs: 12, children: _jsx(Paper, { elevation: 0, children: _jsxs(Box, { p: 2, id: "fulfillmentInfo", children: [settings.showCarMake ? (_jsx(Typography, { variant: "h5", id: "fulfillmentObject", children: result.carMake + " " + result.carModel })) : null, _jsx(Box, { p: 1, component: "span", fontWeight: "fontWeightBold", children: _jsx(FormattedMessage, { id: "fulfillment-results-header", defaultMessage: "Fulfillment amount per {searchDate, date, long} at {regNumber}", values: {
                                            regNumber: result.registrationNumber,
                                            searchDate: x
                                                ? new Date(x.fulfillmentDate)
                                                : new Date()
                                        } }) }), _jsxs(Box, { p: 1, children: [_jsx(Box, { width: 60, display: "inline-block", children: intl.formatMessage(messages.amountLabel) }), _jsx(Box, { component: "span", fontWeight: "fontWeightBold", id: "fulfillmentAmount", children: _jsx(FormattedNumber, { value: x.amount, format: "currency" }) })] }), _jsxs(Box, { p: 1, children: [_jsx(Box, { children: intl.formatMessage(messages.accountInfoHeader) }), _jsxs(Box, { children: [_jsx(Box, { width: 60, display: "inline-block", children: intl.formatMessage(messages.accountLabel) }), _jsx(Box, { component: "span", fontWeight: "fontWeightBold", id: "fulfillmentAccount", children: result.accountNumber })] }), _jsx(Box, { children: result.customerIdentification ? (_jsxs(_Fragment, { children: [_jsx(Box, { width: 60, display: "inline-block", children: intl.formatMessage(messages.customerIdentificationLabel) }), _jsx(Box, { component: "span", fontWeight: "fontWeightBold", id: "fulfillmentCustomerId", children: result.customerIdentification })] })) : (_jsx(FormattedMessage, { id: "fulfillment-customer-identification-missing", defaultMessage: "{label} {regNumber}", values: {
                                                    label: messages.markWithMessage,
                                                    regNumber: result.registrationNumber
                                                } })) }), _jsx(Box, { p: 1, display: "flex", alignItems: "center", style: { gap: 8 }, children: _jsx(FormattedMessage, { id: "fulfillment-warning", defaultMessage: "{icon} {message}", values: {
                                                    icon: _jsx(WarningRounded, { color: "secondary" }),
                                                    message: warningMessage ??
                                                        intl.formatMessage(messages.warningMessage)
                                                } }) })] })] }) }) }, i));
            })) : result?.invalidContractType ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(Paper, { elevation: 0, children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: "h6", children: intl.formatMessage(messages.invalidContractTypeMessage) }) }) }) })) : result ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(Paper, { elevation: 0, children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: "h6", children: intl.formatMessage(messages.noContractFoundMessage) }) }) }) })) : null) : (_jsx(Grid, { item: true, xs: 12, children: _jsx(Paper, { elevation: 0, children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: "h6", children: errorMessage ?? intl.formatMessage(messages.noResultsMessage) }) }) }) }))] }));
};
//# sourceMappingURL=Fulfillment.js.map